import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import OrderPaymentChip from './OrderPaymentChip';
import PaymentStatusControl from './PaymentStatusControl';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import OrderRow from './OrderRow';
import OrderStatusSelect from './OrderStatusSelect';
import {
  downloadOrderReceipt,
  downloadPendingOrderReceipts,
  downloadPendingProductionNotes,
  downloadProductionNotes,
  downloadSelectedOrderReceipts,
  downloadSelectedProductionNotes
} from './orderReceiptDownload';
import { ORDER_STATUS_OPTIONS } from './orderStatus';
import {
  formatOrderDate,
  formatOrderMoney,
  getAddressLines,
  getCustomerName,
  getOrderNumber
} from './orderUtils';

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { search: '', from: '', to: '', status: '', payment: '' };

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({ pageCount: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [savingStatusIds, setSavingStatusIds] = useState([]);
  const [paymentError, setPaymentError] = useState('');
  const [savingPaymentIds, setSavingPaymentIds] = useState([]);
  const [downloadingReceiptIds, setDownloadingReceiptIds] = useState([]);
  const [receiptError, setReceiptError] = useState('');
  const [isDownloadingPendingReceipts, setIsDownloadingPendingReceipts] = useState(false);
  const [isDownloadingPendingProductionNotes, setIsDownloadingPendingProductionNotes] = useState(false);
  const [downloadingProductionNoteIds, setDownloadingProductionNoteIds] = useState([]);
  const [productionNotesError, setProductionNotesError] = useState('');
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [isUpdatingSelectedStatuses, setIsUpdatingSelectedStatuses] = useState(false);
  const [isDownloadingSelectedReceipts, setIsDownloadingSelectedReceipts] = useState(false);
  const [isDownloadingSelectedProductionNotes, setIsDownloadingSelectedProductionNotes] = useState(false);
  const [ordersToDeleteInBulk, setOrdersToDeleteInBulk] = useState(null);
  const [isDeletingSelectedOrders, setIsDeletingSelectedOrders] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    const loadOrders = async () => {
      setIsLoading(true);
      setError('');
      try {
        const params = { page, pageSize: PAGE_SIZE };
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const { data } = await axios.get('/orders', {
          params
        });
        if (!isCurrent) return;
        setOrders(data.orders || []);
        setPagination(data.pagination || { pageCount: 1, total: 0 });
      } catch (requestError) {
        if (isCurrent) setError('Поръчките не можаха да бъдат заредени. Моля, опитайте отново.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadOrders();
    return () => {
      isCurrent = false;
    };
  }, [filters, page]);

  const hasFilters = Object.values(filters).some(Boolean);
  const isValidRange =
    !draftFilters.from || !draftFilters.to || draftFilters.from <= draftFilters.to;
  const selectedOrders = orders.filter((order) => selectedOrderIds.includes(order.id));
  const areAllOrdersSelected = orders.length > 0 && selectedOrders.length === orders.length;

  const updateFilter = (field) => (event) => {
    setDraftFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (!isValidRange) return;
    setPage(1);
    setFilters({ ...draftFilters });
    setSelectedOrderIds([]);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setSelectedOrderIds([]);
  };

  const openOrder = (id) => navigate(`/orderDetails/${id}`);

  const toggleOrderSelection = (id) => {
    setSelectedOrderIds((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    );
  };

  const toggleAllOrderSelections = () => {
    setSelectedOrderIds(areAllOrdersSelected ? [] : orders.map((order) => order.id));
  };

  const downloadSelectedReceipts = async () => {
    if (!selectedOrders.length) return;
    setReceiptError('');
    setIsDownloadingSelectedReceipts(true);
    try {
      await downloadSelectedOrderReceipts(selectedOrders);
    } catch (requestError) {
      setReceiptError('Дигиталните бележки за избраните поръчки не можаха да бъдат изтеглени.');
    } finally {
      setIsDownloadingSelectedReceipts(false);
    }
  };

  const downloadSelectedFactoryNotes = async () => {
    if (!selectedOrders.length) return;
    setProductionNotesError('');
    setIsDownloadingSelectedProductionNotes(true);
    try {
      await downloadSelectedProductionNotes(selectedOrders);
    } catch (requestError) {
      setProductionNotesError('Бележките за производство за избраните поръчки не можаха да бъдат изтеглени.');
    } finally {
      setIsDownloadingSelectedProductionNotes(false);
    }
  };

  const updateSelectedOrderStatuses = async () => {
    if (!selectedOrders.length || !bulkStatus) return;
    setStatusError('');
    setIsUpdatingSelectedStatuses(true);
    try {
      const { data } = await axios.patch('/orders/status', {
        status: bulkStatus,
        orders: selectedOrders.map((order) => ({ id: order.id, version: order.version || 0 }))
      });
      const updatedOrders = new Map((data.orders || []).map((order) => [order.id, order]));
      setOrders((current) => current.map((order) => updatedOrders.get(order.id) || order));
      setSelectedOrderIds([]);
      setBulkStatus('');
    } catch (requestError) {
      setStatusError(
        requestError.response?.status === 409
          ? 'Една от избраните поръчки е променена от друг администратор. Презаредете страницата.'
          : 'Статусът на избраните поръчки не можа да бъде запазен.'
      );
    } finally {
      setIsUpdatingSelectedStatuses(false);
    }
  };

  const changeOrderStatus = async (order, status) => {
    if (status === order.status) return;
    setStatusError('');
    setSavingStatusIds((current) => [...current, order.id]);
    setOrders((current) =>
      current.map((item) => (item.id === order.id ? { ...item, status } : item))
    );
    try {
      const { data } = await axios.patch(`/orders/${order.id}/status`, {
        status,
        version: order.version || 0
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? data.order : item)));
    } catch (requestError) {
      setOrders((current) => current.map((item) => (item.id === order.id ? order : item)));
      setStatusError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : 'Статусът не можа да бъде запазен.'
      );
    } finally {
      setSavingStatusIds((current) => current.filter((id) => id !== order.id));
    }
  };

  const changePaymentStatus = async (order, paymentStatus) => {
    if (paymentStatus === order.paymentStatus) return;
    setPaymentError('');
    setSavingPaymentIds((current) => [...current, order.id]);
    try {
      const { data } = await axios.patch(`/orders/${order.id}/payment-status`, {
        paymentStatus,
        version: order.version || 0
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? data.order : item)));
    } catch (requestError) {
      setPaymentError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : 'Статусът на плащането не можа да бъде запазен.'
      );
    } finally {
      setSavingPaymentIds((current) => current.filter((id) => id !== order.id));
    }
  };

  const downloadReceipt = async (order) => {
    setReceiptError('');
    setDownloadingReceiptIds((current) => [...current, order.id]);
    try {
      await downloadOrderReceipt(order);
    } catch (requestError) {
      setReceiptError('Дигиталната бележка не можа да бъде изтеглена. Моля, опитайте отново.');
    } finally {
      setDownloadingReceiptIds((current) => current.filter((id) => id !== order.id));
    }
  };

  const downloadPendingReceipts = async () => {
    setReceiptError('');
    setIsDownloadingPendingReceipts(true);
    try {
      await downloadPendingOrderReceipts();
    } catch (requestError) {
      setReceiptError(
        requestError.response?.status === 404
          ? 'Няма чакащи поръчки за изтегляне.'
          : 'Дигиталните бележки не можаха да бъдат изтеглени. Моля, опитайте отново.'
      );
    } finally {
      setIsDownloadingPendingReceipts(false);
    }
  };

  const downloadPendingFactoryNotes = async () => {
    setProductionNotesError('');
    setIsDownloadingPendingProductionNotes(true);
    try {
      await downloadPendingProductionNotes();
    } catch (requestError) {
      setProductionNotesError(
        requestError.response?.status === 404
          ? 'Няма чакащи поръчки за изтегляне.'
          : 'Бележките за производство не можаха да бъдат изтеглени. Моля, опитайте отново.'
      );
    } finally {
      setIsDownloadingPendingProductionNotes(false);
    }
  };

  const downloadFactoryNotes = async (order) => {
    setProductionNotesError('');
    setDownloadingProductionNoteIds((current) => [...current, order.id]);
    try {
      await downloadProductionNotes(order);
    } catch (requestError) {
      setProductionNotesError(
        'Бележките за производство не можаха да бъдат изтеглени. Моля, опитайте отново.'
      );
    } finally {
      setDownloadingProductionNoteIds((current) => current.filter((id) => id !== order.id));
    }
  };

  const requestDelete = (order) => {
    setDeleteError('');
    setDeleteSuccess('');
    setOrderToDelete(order);
  };

  const closeDeleteDialog = () => {
    if (!deletingOrderId) setOrderToDelete(null);
  };

  const deleteOrder = async () => {
    if (!orderToDelete) return;

    const order = orderToDelete;
    setDeleteError('');
    setDeletingOrderId(order.id);
    try {
      await axios.delete(`/orders/${order.id}`, {
        data: { version: order.version || 0 }
      });
      setOrders((current) => current.filter((item) => item.id !== order.id));
      setSelectedOrderIds((current) => current.filter((id) => id !== order.id));
      setPagination((current) => {
        const total = Math.max(0, current.total - 1);
        return { ...current, total, pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)) };
      });
      setOrderToDelete(null);
      setDeleteSuccess(`Поръчка #${getOrderNumber(order)} беше изтрита.`);

      // When the deletion leaves a later page empty, load the preceding page.
      if (orders.length === 1 && page > 1) setPage((current) => current - 1);
    } catch (requestError) {
      setDeleteError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : requestError.response?.status === 404
            ? 'Поръчката вече не е налична.'
            : 'Поръчката не можа да бъде изтрита. Моля, опитайте отново.'
      );
    } finally {
      setDeletingOrderId(null);
    }
  };

  const requestBulkDelete = () => {
    if (!selectedOrders.length) return;
    setDeleteError('');
    setDeleteSuccess('');
    setOrdersToDeleteInBulk(selectedOrders);
  };

  const closeBulkDeleteDialog = () => {
    if (!isDeletingSelectedOrders) setOrdersToDeleteInBulk(null);
  };

  const deleteSelectedOrders = async () => {
    if (!ordersToDeleteInBulk?.length) return;

    const ordersToDelete = ordersToDeleteInBulk;
    setDeleteError('');
    setIsDeletingSelectedOrders(true);
    try {
      const { data } = await axios.delete('/orders', {
        data: {
          orders: ordersToDelete.map((order) => ({ id: order.id, version: order.version || 0 }))
        }
      });
      const deletedIds = new Set(data.deletedIds || ordersToDelete.map((order) => order.id));
      const deletedCount = deletedIds.size;
      setOrders((current) => current.filter((order) => !deletedIds.has(order.id)));
      setSelectedOrderIds([]);
      setPagination((current) => {
        const total = Math.max(0, current.total - deletedCount);
        return { ...current, total, pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)) };
      });
      setOrdersToDeleteInBulk(null);
      setDeleteSuccess(
        deletedCount === 1 ? 'Поръчката беше изтрита.' : `Успешно изтрити поръчки: ${deletedCount}.`
      );

      if (orders.length === ordersToDelete.length && page > 1) {
        setPage((current) => current - 1);
      }
    } catch (requestError) {
      setDeleteError(
        requestError.response?.status === 409
          ? 'Една от избраните поръчки е променена от друг администратор. Презаредете страницата.'
          : requestError.response?.status === 404
            ? 'Една от избраните поръчки вече не е налична.'
            : 'Избраните поръчки не можаха да бъдат изтрити. Моля, опитайте отново.'
      );
    } finally {
      setIsDeletingSelectedOrders(false);
    }
  };

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Поръчки"
        subtitle={`${hasFilters ? 'Намерени' : 'Всички клиентски'} поръчки (${pagination.total}).`}
        icon={ShoppingBagRoundedIcon}
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadPendingReceipts}
              disabled={isDownloadingPendingReceipts}
            >
              {isDownloadingPendingReceipts
                ? 'Подготвяне на бележките…'
                : 'Свали дигитални бележки'}
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCutRoundedIcon />}
              onClick={downloadPendingFactoryNotes}
              disabled={isDownloadingPendingProductionNotes}
            >
              {isDownloadingPendingProductionNotes
                ? 'Подготвяне на бележките…'
                : 'Свали бележки за производство'}
            </Button>
          </Stack>
        }
      />

      <Card
        component="form"
        onSubmit={applyFilters}
        sx={{ mb: 3, overflow: 'visible', p: { xs: 2.5, md: 3 } }}
      >
        <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
          <FilterAltRoundedIcon color="primary" fontSize="small" />
          <Typography variant="h6">Филтри</Typography>
        </Stack>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              label="Търсене"
              placeholder="Поръчка, име, имейл или телефон"
              value={draftFilters.search}
              onChange={updateFilter('search')}
              inputProps={{ maxLength: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <TextField
              fullWidth
              type="date"
              label="От дата"
              value={draftFilters.from}
              onChange={updateFilter('from')}
              InputLabelProps={{ shrink: true }}
              error={!isValidRange}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <TextField
              fullWidth
              type="date"
              label="До дата"
              value={draftFilters.to}
              onChange={updateFilter('to')}
              InputLabelProps={{ shrink: true }}
              error={!isValidRange}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Статус</InputLabel>
              <Select label="Статус" value={draftFilters.status} onChange={updateFilter('status')}>
                <MenuItem value="">Всички</MenuItem>
                {ORDER_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Плащане</InputLabel>
              <Select
                label="Плащане"
                value={draftFilters.payment}
                onChange={updateFilter('payment')}
                SelectDisplayProps={{ 'aria-label': 'Плащане' }}
              >
                <MenuItem value="">Всички</MenuItem>
                <MenuItem value="delivery">Плащане при доставка</MenuItem>
                <MenuItem value="bank">Банков превод</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent={{ sm: 'flex-end' }} spacing={1}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValidRange}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Филтрирай
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={clearFilters}
                disabled={!Object.values(draftFilters).some(Boolean) && !hasFilters}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Изчисти
              </Button>
            </Stack>
          </Grid>
        </Grid>
        {!isValidRange && (
          <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
            Началната дата трябва да бъде преди крайната дата.
          </Typography>
        )}
      </Card>

      {selectedOrders.length > 0 && (
        <Card
          aria-label="Действия за избраните поръчки"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 6,
            mb: 2,
            p: { xs: 2, md: 2.5 },
            position: 'sticky',
            top: { xs: 8, md: 16 },
            zIndex: (theme) => theme.zIndex.appBar
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} alignItems={{ xl: 'center' }} gap={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography fontWeight={750} whiteSpace="nowrap">
                Избрани: {selectedOrders.length}
              </Typography>
              <Button size="small" onClick={() => setSelectedOrderIds([])}>
                Изчисти избора
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={1} flex={1}>
              <Button
                variant="outlined"
                startIcon={
                  isDownloadingSelectedReceipts ? <CircularProgress size={16} /> : <DownloadRoundedIcon />
                }
                onClick={downloadSelectedReceipts}
                disabled={isDownloadingSelectedReceipts}
              >
                Свали бележки
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  isDownloadingSelectedProductionNotes ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ContentCutRoundedIcon />
                  )
                }
                onClick={downloadSelectedFactoryNotes}
                disabled={isDownloadingSelectedProductionNotes}
              >
                Свали бележки за производство
              </Button>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="bulk-order-status-label">Нов статус</InputLabel>
                <Select
                  label="Нов статус"
                  labelId="bulk-order-status-label"
                  value={bulkStatus}
                  onChange={(event) => setBulkStatus(event.target.value)}
                >
                  {ORDER_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={updateSelectedOrderStatuses}
                disabled={!bulkStatus || isUpdatingSelectedStatuses}
                startIcon={isUpdatingSelectedStatuses ? <CircularProgress color="inherit" size={16} /> : null}
              >
                Обнови статус
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={requestBulkDelete}
                disabled={isDeletingSelectedOrders}
              >
                Изтрий избраните
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {statusError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError('')}>
          {statusError}
        </Alert>
      )}
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPaymentError('')}>
          {paymentError}
        </Alert>
      )}
      {receiptError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReceiptError('')}>
          {receiptError}
        </Alert>
      )}
      {productionNotesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProductionNotesError('')}>
          {productionNotesError}
        </Alert>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
          {deleteError}
        </Alert>
      )}

      {isLoading ? (
        <Card
          sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 260 }}
        >
          <CircularProgress aria-label="Зареждане на поръчките" />
        </Card>
      ) : !error && orders.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <ShoppingBagRoundedIcon sx={{ color: 'grey.300', fontSize: 52, mb: 1 }} />
          <Typography variant="h6">
            {hasFilters ? 'Няма поръчки, които отговарят на филтрите' : 'Все още няма поръчки'}
          </Typography>
        </Card>
      ) : !error ? (
        <>
          <TableContainer
            component={Card}
            aria-label="Хоризонтално превъртаем списък с поръчки"
            role="region"
            tabIndex={0}
            sx={{
              display: { xs: 'none', lg: 'block' },
              maxWidth: '100%',
              overflowX: 'scroll',
              overflowY: 'hidden',
              scrollbarGutter: 'stable',
              '&:focus-visible': {
                outline: '3px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2
              }
            }}
          >
            <Table aria-label="Списък с поръчки" sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell
                    padding="checkbox"
                    sx={{
                      bgcolor: 'background.paper',
                      left: 0,
                      minWidth: 56,
                      position: 'sticky',
                      zIndex: 4
                    }}
                  >
                    <Checkbox
                      checked={areAllOrdersSelected}
                      indeterminate={selectedOrders.length > 0 && !areAllOrdersSelected}
                      inputProps={{ 'aria-label': 'Избери всички поръчки на тази страница' }}
                      onChange={toggleAllOrderSelections}
                    />
                  </TableCell>
                  <TableCell>Поръчка и дата</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '8px 0 10px -10px rgba(15, 23, 42, 0.55)',
                      left: 56,
                      minWidth: 240,
                      position: 'sticky',
                      isolation: 'isolate',
                      zIndex: 3
                    }}
                  >
                    Клиент
                  </TableCell>
                  <TableCell>Адрес</TableCell>
                  <TableCell>Предпочитано плащане</TableCell>
                  <TableCell>Стойност</TableCell>
                  <TableCell>Редактирана</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onOpen={openOrder}
                    onStatusChange={changeOrderStatus}
                    isStatusSaving={savingStatusIds.includes(order.id)}
                    onDownloadReceipt={downloadReceipt}
                    isReceiptDownloading={downloadingReceiptIds.includes(order.id)}
                    onDownloadProductionNotes={downloadFactoryNotes}
                    isProductionNotesDownloading={downloadingProductionNoteIds.includes(order.id)}
                    onPaymentStatusChange={changePaymentStatus}
                    isPaymentSaving={savingPaymentIds.includes(order.id)}
                    onDelete={requestDelete}
                    isSelected={selectedOrderIds.includes(order.id)}
                    onToggleSelected={toggleOrderSelection}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack spacing={2} sx={{ display: { xs: 'flex', lg: 'none' } }}>
            {orders.map((order) => (
              <Card
                key={order.id}
                sx={{ p: 2.5, userSelect: 'text' }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox
                      checked={selectedOrderIds.includes(order.id)}
                      inputProps={{ 'aria-label': `Избери поръчка ${getOrderNumber(order)}` }}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleOrderSelection(order.id)}
                      onKeyDown={(event) => event.stopPropagation()}
                    />
                    <Box>
                      <Typography fontWeight={800}>#{getOrderNumber(order)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatOrderDate(order.createdAt)}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label="Отвори поръчката"
                    onClick={() => openOrder(order.id)}
                    title="Отвори поръчката"
                  >
                    <ArrowForwardRoundedIcon />
                  </IconButton>
                </Stack>

                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2, pt: 2 }}>
                  <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                    {order.editedAt && <Chip size="small" color="secondary" label="Редактирана" />}
                  </Stack>
                  <OrderStatusSelect
                    value={order.status}
                    onChange={(status) => changeOrderStatus(order, status)}
                    disabled={savingStatusIds.includes(order.id)}
                    label={`Статус на поръчка ${getOrderNumber(order)}`}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      downloadReceipt(order);
                    }}
                    disabled={downloadingReceiptIds.includes(order.id)}
                    sx={{ mt: 1.5 }}
                  >
                    {downloadingReceiptIds.includes(order.id)
                      ? 'Подготвяне на бележка…'
                      : 'Дигитална бележка'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCutRoundedIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      downloadFactoryNotes(order);
                    }}
                    disabled={downloadingProductionNoteIds.includes(order.id)}
                    sx={{ mt: 1 }}
                  >
                    {downloadingProductionNoteIds.includes(order.id)
                      ? 'Подготвяне на бележки…'
                      : 'Бележки за производство'}
                  </Button>
                  <Button
                    fullWidth
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      requestDelete(order);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Изтрий поръчката
                  </Button>
                  <Typography fontWeight={650} sx={{ mt: 2 }}>
                    {getCustomerName(order)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[order.email, order.phone].filter(Boolean).join(' · ') ||
                      'Няма данни за контакт'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {getAddressLines(order).join(', ') || 'Няма въведен адрес'}
                  </Typography>
                  {order.shippingType && (
                    <Typography variant="caption" color="text.secondary">
                      {order.shippingType}
                    </Typography>
                  )}
                </Box>

                <Stack mt={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Плащане
                    </Typography>
                    <OrderPaymentChip paymentType={order.paymentType} bankPayment={order.isBankPayment} />
                    {order.isBankPayment && (
                      <Box sx={{ mt: 1 }}>
                        <PaymentStatusControl
                          paymentStatus={order.paymentStatus}
                          onChange={(paymentStatus) => changePaymentStatus(order, paymentStatus)}
                          saving={savingPaymentIds.includes(order.id)}
                        />
                      </Box>
                    )}
                  </Box>
                </Stack>

                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, mt: 2, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Стойност
                  </Typography>
                  <Stack spacing={0.5} mt={0.75}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Междинна сума
                      </Typography>
                      <Typography variant="body2" fontWeight={800}>
                        {formatOrderMoney(order.subtotal, order.currency)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Доставка
                      </Typography>
                      <Typography variant="body2">
                        {formatOrderMoney(order.shippingPrice, order.currency)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Общо
                      </Typography>
                      <Typography variant="body2">
                        {formatOrderMoney(order.total, order.currency)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Card>
            ))}
          </Stack>

          {pagination.pageCount > 1 && (
            <Stack alignItems="center" mt={4} spacing={1}>
              <Pagination
                page={page}
                count={pagination.pageCount}
                color="primary"
                onChange={(event, nextPage) => {
                  setSelectedOrderIds([]);
                  setPage(nextPage);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {pagination.total} поръчки общо
              </Typography>
            </Stack>
          )}
        </>
      ) : null}

      <Snackbar
        open={Boolean(deleteSuccess)}
        autoHideDuration={5000}
        onClose={() => setDeleteSuccess('')}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setDeleteSuccess('')}>
          {deleteSuccess}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(orderToDelete)}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-order-dialog-title"
      >
        <DialogTitle id="delete-order-dialog-title">Изтриване на поръчка</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography component="p" color="inherit" sx={{ m: 0 }}>
              Сигурни ли сте, че искате да изтриете поръчка #{
                orderToDelete ? getOrderNumber(orderToDelete) : ''
              }? Тя ще бъде премахната от списъка, но ще остане в системата за справка.
            </Typography>
            {orderToDelete && (
              <Box
                aria-label="Клиент на поръчката"
                sx={{ bgcolor: 'grey.50', borderRadius: 1.5, mt: 2, px: 1.5, py: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Клиент
                </Typography>
                <Typography fontWeight={700}>{getCustomerName(orderToDelete)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {orderToDelete.email || 'Няма имейл'}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingOrderId)}>
            Отказ
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteOrder}
            disabled={Boolean(deletingOrderId)}
            startIcon={deletingOrderId ? <CircularProgress color="inherit" size={16} /> : null}
          >
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(ordersToDeleteInBulk?.length)}
        onClose={closeBulkDeleteDialog}
        aria-labelledby="delete-selected-orders-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="delete-selected-orders-dialog-title">Изтриване на избрани поръчки</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography component="p" color="inherit" sx={{ m: 0 }}>
              Сигурни ли сте, че искате да изтриете {ordersToDeleteInBulk?.length || 0} избрани поръчки?
              Те ще бъдат премахнати от списъка, но ще останат в системата за справка.
            </Typography>
            <Box
              aria-label="Избрани поръчки за изтриване"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                maxHeight: 280,
                mt: 2,
                overflowY: 'auto'
              }}
            >
              {ordersToDeleteInBulk?.map((order) => (
                <Box
                  key={order.id}
                  sx={{
                    '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                    px: 1.5,
                    py: 1
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Поръчка #{getOrderNumber(order)}
                  </Typography>
                  <Typography fontWeight={700}>{getCustomerName(order)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.email || 'Няма имейл'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDeleteDialog} disabled={isDeletingSelectedOrders}>
            Отказ
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteSelectedOrders}
            disabled={isDeletingSelectedOrders}
            startIcon={isDeletingSelectedOrders ? <CircularProgress color="inherit" size={16} /> : null}
          >
            Изтрий избраните
          </Button>
        </DialogActions>
      </Dialog>
    </VendorDashboardLayout>
  );
}
